import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import ResponseError from "#/types/responseError"

type SchemaRef = { $ref?: string; type?: string }
type SchemaProp = {
  title?: string
  description?: string
  type?: string | string[]
  format?: string
  enum?: Array<string | number>
  ["x-enumNames"]?: string[]
  ["x-enum-descriptions"]?: string[]
  oneOf?: SchemaRef[]
  $ref?: string
  items?: SchemaRef
  properties?: Record<string, SchemaProp>
}
export type MemberContextSchema = {
  properties?: Record<string, SchemaProp>
  definitions?: Record<string, SchemaProp>
}

export type EnumOption = { value: string; label: string }
export type DateFormat = "date" | "date-time"
export type ScalarKind = "boolean" | "nullable-boolean" | "number"

export function useMemberContextSchema(): UseQueryResult<MemberContextSchema, ResponseError> {
  return useQuery<MemberContextSchema, ResponseError>({
    queryKey: ["member-context-schema"],
    queryFn: async () => (await carrotClient.getMemberContextSchema()) as MemberContextSchema,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3)
  })
}

// Visits every field under the schema's `body` definition, depth-first, with its body-relative path, the
// property as written, and the definition its $ref resolves to (if any).
function walkBodyFields(
  schema: MemberContextSchema,
  visit: (path: string[], prop: SchemaProp, resolved: SchemaProp | undefined) => void
): void {
  const definitions = schema.definitions ?? {}
  const resolve = (ref?: string): SchemaProp | undefined =>
    ref ? definitions[ref.replace("#/definitions/", "")] : undefined
  const refOf = (prop: SchemaProp): string | undefined => prop.$ref ?? prop.oneOf?.find((o) => o.$ref)?.$ref

  walk(resolve(refOf(schema.properties?.body ?? {}))?.properties ?? {}, [])

  function walk(properties: Record<string, SchemaProp>, path: string[]): void {
    for (const [key, prop] of Object.entries(properties)) {
      const fieldPath = [...path, key]
      const resolved = resolve(refOf(prop))
      visit(fieldPath, prop, resolved)
      if (resolved?.properties) {
        walk(resolved.properties, fieldPath)
      }
    }
  }
}

function toOptions(source: SchemaProp): EnumOption[] {
  const values = source.enum ?? []
  const descriptions = source["x-enum-descriptions"] ?? []
  const names = source["x-enumNames"] ?? []
  return values.map((value, i) => ({ value: String(value), label: descriptions[i] ?? names[i] ?? String(value) }))
}

// body-relative dotted field path -> enum options, for every enum field. Non-enum fields are absent.
export function buildEnumOptions(schema: MemberContextSchema): Map<string, EnumOption[]> {
  const map = new Map<string, EnumOption[]>()
  walkBodyFields(schema, (path, prop, resolved) => {
    const enumSource = Array.isArray(prop.enum) ? prop : resolved?.enum ? resolved : undefined
    if (enumSource) {
      map.set(path.join("."), toOptions(enumSource))
    }
  })
  return map
}

// body-relative dotted field path -> date format, for every date / date-time field.
export function buildDateFields(schema: MemberContextSchema): Map<string, DateFormat> {
  const map = new Map<string, DateFormat>()
  walkBodyFields(schema, (path, prop, resolved) => {
    const format = prop.format ?? resolved?.format
    if (format === "date" || format === "date-time") {
      map.set(path.join("."), format)
    }
  })
  return map
}

function typeSet(prop: SchemaProp, resolved: SchemaProp | undefined): Set<string> {
  const types = new Set<string>()
  for (const type of [prop.type, resolved?.type]) {
    if (Array.isArray(type)) type.forEach((entry) => types.add(entry))
    else if (type) types.add(type)
  }
  return types
}

// body-relative dotted field path -> scalar input kind, for boolean and numeric (non-enum) fields.
export function buildScalarKinds(schema: MemberContextSchema): Map<string, ScalarKind> {
  const map = new Map<string, ScalarKind>()
  walkBodyFields(schema, (path, prop, resolved) => {
    if (prop.enum || resolved?.enum) return
    const types = typeSet(prop, resolved)
    if (types.has("boolean")) {
      map.set(path.join("."), types.has("null") ? "nullable-boolean" : "boolean")
    } else if (types.has("integer") || types.has("number")) {
      map.set(path.join("."), "number")
    }
  })
  return map
}

// body-relative dotted field path -> the schema's display name (title), for every field that has one.
export function buildFieldLabels(schema: MemberContextSchema): Map<string, string> {
  const map = new Map<string, string>()
  walkBodyFields(schema, (path, prop) => {
    if (prop.title) {
      map.set(path.join("."), prop.title)
    }
  })
  return map
}

// body-relative dotted field path -> Set of paths whose enum value is nullable (allows null).
// Nullability can come from prop.type including "null", or from a oneOf null branch.
export function buildNullableEnumPaths(schema: MemberContextSchema): Set<string> {
  const set = new Set<string>()
  walkBodyFields(schema, (path, prop, resolved) => {
    const enumSource = Array.isArray(prop.enum) ? prop : resolved?.enum ? resolved : undefined
    if (!enumSource) return
    const hasNullType = typeSet(prop, resolved).has("null")
    const hasNullBranch =
      (prop.oneOf?.some((o) => o.type === "null") ?? false) ||
      (resolved?.oneOf?.some((o) => o.type === "null") ?? false)
    if (hasNullType || hasNullBranch) {
      set.add(path.join("."))
    }
  })
  return set
}

// body-relative dotted field path -> the schema's description, for every field that has one.
export function buildFieldDescriptions(schema: MemberContextSchema): Map<string, string> {
  const map = new Map<string, string>()
  walkBodyFields(schema, (path, prop) => {
    if (prop.description) {
      map.set(path.join("."), prop.description)
    }
  })
  return map
}
